import {
  BoundedContent,
  Box,
  Column,
  GridContainer,
  Link,
  Row,
  Typography
} from "@vp/swan";
import React, { useState } from "react";
import { Trans } from "react-i18next";
import { useTranslation } from "../../useTranslation";
import { useNotificationContext } from "../../NotificationContext";
import WithSocialProviders from "../Components/withSocialProviders";
import { AuthFlowBuilder, AuthFlowParams } from "@monorepo/libraries";
import { Screens } from "../ScreensLayout";
import SignUpForm from "./SignUpForm";

import { pushDataToSegment } from "../../segmentData";
import { ACCOUNT_CREATION_PAGE, EU_EXPERIMENT_MAP, EXPERIMENT_MAP, NAVIGATION_CLICKED, SIGN_IN_LABEL, SIGN_IN_LINK_CLICKED, SITE_VCS, VISTAPRINT_ONLY_BRAND } from "../../constant";

export interface LayoutProps {
  onChangeScreen: (screen: Screens) => void;
  notificationRef?: React.RefObject<HTMLDivElement>;
  isSocialLoginDisabled: (locale: string) => boolean;
  wauth?: any;
  isAutomaticAgreementEnabled: boolean;
}

const SignUp = (props: LayoutProps) => {
  const [t] = useTranslation("signUp");

  const { onChangeScreen, notificationRef, isSocialLoginDisabled, wauth, isAutomaticAgreementEnabled } = props;

  const { clearNotification } = useNotificationContext();
  const [showSocialSignIn, setShowSocialSignIn] = useState(true);
  const authParams: AuthFlowParams = new AuthFlowBuilder().build();
  const [showLoginWithEmail, setShowLoginWithEmail] = useState(false);
  const isSocialLoginDisabledForLocale = isSocialLoginDisabled(
    authParams.culture || ""
  );

  React.useEffect(() => {
    clearNotification();
    document.title = t("documentTitle");
    if ((
      (EXPERIMENT_MAP.get(((wauth || {}).site || "").toLowerCase()) || []).includes((wauth || {}).culture) ||
      (EU_EXPERIMENT_MAP.get(((wauth || {}).site || "").toLowerCase()) || []).includes((wauth || {}).culture)
    ) &&
       isAutomaticAgreementEnabled) {
      pushDataToSegment(ACCOUNT_CREATION_PAGE, true, { experiment_name: `${(wauth || {}).culture}-randomized-email-preference-opt-in`, experiment_value: "true" });
    } else {
      pushDataToSegment(ACCOUNT_CREATION_PAGE, true);
    }
  }, []);

  const handleOnClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    pushDataToSegment(NAVIGATION_CLICKED, false, { label: SIGN_IN_LABEL, eventDetail: ACCOUNT_CREATION_PAGE, navigationDetail: SIGN_IN_LINK_CLICKED, pageName: ACCOUNT_CREATION_PAGE });
    onChangeScreen(Screens.SignIn);
  };

  const getHeadingText = () => {
    return (
      <Box mb="to-actions">
        <Typography
          textAlign="left"
          data-testid="signUpPrimaryHeading"
          fontSkin="title-headline"
        >
          {wauth.site === SITE_VCS ? t("headings.vcs") : t("headings.primary")}
        </Typography>
      </Box>
    );
  };

  const getIndianLocaleMessage = () => {
    return (
      <>
       <Typography textAlign="left" fontSkin="footnote" data-testid="automaticAgreement" >
        <Trans i18nKey="signUp:exclusiveEmails.automaticAgreement">
          <a href={t("termsAndPrivacy.termsOfServicesLink", { culture: "en-IN" })} target="_blank" rel="noopener noreferrer" />
          <a href={t("termsAndPrivacy.privacyPolicyLink", { culture: "en-IN" })} target="_blank" rel="noopener noreferrer" />
        </Trans>
      </Typography>
      </>
    );
  };

  return (
    <BoundedContent>
      <GridContainer>
        <Row>
          <Column
            span={12}
            offset={0}
            spanSm={8}
            offsetSm={2}
            spanMd={6}
            offsetMd={3}
            spanLg={4}
            offsetLg={4}
            spanXl={4}
            offsetXl={4}
          >
            {getHeadingText()}
          </Column>
        </Row>
        <WithSocialProviders
          showLoginWithEmail={showLoginWithEmail}
          setShowLoginWithEmail={setShowLoginWithEmail}
          setShowSocialSignIn={setShowSocialSignIn}
          showSocialSignIn={showSocialSignIn}
          context="signUp"
          isSocialLoginDisabledForLocale={isSocialLoginDisabledForLocale}
        >
          {!showLoginWithEmail && (
            <>
              {!isSocialLoginDisabledForLocale && (
                <Box mb={4}>
                  <Typography textAlign="left" fontSkin="title-subsection">
                    {t("minorHeading") }
                  </Typography>
                </Box>
              )}
              <SignUpForm
                notificationRef={notificationRef}
                setShowSocialSignIn={setShowSocialSignIn}
                wauth={wauth}
                onChangeScreen={onChangeScreen}
                setShowLoginWithEmail={setShowLoginWithEmail}
                isAutomaticAgreementEnabled={isAutomaticAgreementEnabled}
              />
            </>
          )}
          <Typography textAlign="center" fontSkin="body-standard">
            {t("info") }{" "}
            <Link
              href="/#"
              onClick={handleOnClick}
              data-testid="signInTabTitle"
              fontSkin="body-standard"
            >
              {t("signIn") }
            </Link>
          </Typography>
        </WithSocialProviders>

        {((wauth || {}).culture === "en-IN" &&
          VISTAPRINT_ONLY_BRAND.includes((wauth || {}).site.toLowerCase()))
          ? (
          <Box mt="between-subsections">
            <Row>
              <Column
                span={12}
                offset={0}
                spanSm={8}
                offsetSm={2}
                spanMd={6}
                offsetMd={3}
                spanLg={4}
                offsetLg={4}
                spanXl={4}
                offsetXl={4}
              >
                {getIndianLocaleMessage()}
              </Column>
            </Row>
          </Box>
            )
          : null}
      </GridContainer>
    </BoundedContent>
  );
};

export default SignUp;
